import React from 'react';
import { Search, MessageCircle, RotateCcw, Phone, Calendar, BarChart3, Mail, Linkedin, MessageSquare } from 'lucide-react';
import AgentProcessVisualizer from './AgentProcessVisualizer';

const AISalesTeamSection: React.FC = () => {
  const [counts, setCounts] = React.useState<{ [key: number]: number }>({});

  // Counter animation hook
  const useCounter = (end: number, duration: number = 3000, agentId: number) => {
    React.useEffect(() => {
      let startTime: number;
      let animationFrame: number;

      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        
        // Easing function for more realistic counting
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = Math.floor(easeOutQuart * end);
        
        setCounts(prev => ({ ...prev, [agentId]: currentCount }));
        
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          // Restart the animation after a brief pause
          setTimeout(() => {
            setTimeout(() => {
              startTime = 0; // Reset startTime for next animation
              animationFrame = requestAnimationFrame(animate);
            }, 500);
          }, 5000);
        }
      };

      animationFrame = requestAnimationFrame(animate);

      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }, [end, duration, agentId]);

    return counts[agentId] || 0;
  };

  const agents = [
    {
      id: 1,
      name: 'Lead Gen Agent',
      description: 'Sources & Verifies Leads',
      monthlyOutput: '110,000 leads / month',
      targetNumber: 110000,
      unit: 'leads',
      image: '/leadgenagent.png',
      icon: Search,
      processSteps: [
        {
          id: 'search',
          text: 'Searching DB',
          icon: <svg className="w-3 h-3 text-brand-blue animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
          </svg>,
          color: 'brand-blue',
          position: 'top-2 left-2 sm:top-2 sm:left-2',
          animationDelay: '0s'
        },
        {
          id: 'found',
          text: 'Lead Found',
          icon: <svg className="w-3 h-3 text-brand-green-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>,
          color: 'brand-green-teal',
          position: 'top-2 right-2 sm:top-2 sm:right-2',
          animationDelay: '1s'
        },
        {
          id: 'verify',
          text: 'Verifying',
          icon: <svg className="w-3 h-3 text-yellow-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>,
          color: 'yellow-400',
          position: 'top-14 left-2 sm:top-16 sm:left-2',
          animationDelay: '2s'
        },
        {
          id: 'phone',
          text: 'Phone Lookup',
          icon: <svg className="w-3 h-3 text-purple-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>,
          color: 'purple-400',
          position: 'top-14 right-2 sm:top-16 sm:right-2',
          animationDelay: '3s'
        },
        {
          id: 'social',
          text: 'Social Check',
          icon: <svg className="w-3 h-3 text-blue-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ animationDelay: '0.5s' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
          </svg>,
          color: 'blue-400',
          position: 'bottom-8 left-2 sm:bottom-8 sm:left-2',
          animationDelay: '4s'
        },
        {
          id: 'qualified',
          text: 'Qualified',
          icon: <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>,
          color: 'green-400',
          position: 'bottom-8 right-2 sm:bottom-8 sm:right-2',
          animationDelay: '5s'
        }
      ]
    },
    {
      id: 2,
      name: 'Call Agent',
      description: 'Makes Qualifying Calls',
      monthlyOutput: '155,000 calls / month',
      targetNumber: 155000,
      unit: 'calls',
      image: '/callagent.png',
      icon: Phone,
      processSteps: [
        {
          id: 'dialing',
          text: 'Dialing',
          icon: <Phone className="w-3 h-3 text-brand-blue animate-pulse" />,
          color: 'brand-blue',
          position: 'top-2 left-2 sm:top-2 sm:left-2',
          animationDelay: '0s'
        },
        {
          id: 'connecting',
          text: 'Connecting',
          icon: <svg className="w-3 h-3 text-yellow-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>,
          color: 'yellow-400',
          position: 'top-2 right-2 sm:top-2 sm:right-2',
          animationDelay: '1s'
        },
        {
          id: 'discussing',
          text: 'Discussing',
          icon: <MessageCircle className="w-3 h-3 text-brand-green-teal animate-bounce" />,
          color: 'brand-green-teal',
          position: 'top-14 left-2 sm:top-16 sm:left-2',
          animationDelay: '2s'
        },
        {
          id: 'qualifying',
          text: 'Qualifying',
          icon: <svg className="w-3 h-3 text-purple-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>,
          color: 'purple-400',
          position: 'top-14 right-2 sm:top-16 sm:right-2',
          animationDelay: '3s'
        },
        {
          id: 'scheduling',
          text: 'Scheduling',
          icon: <Calendar className="w-3 h-3 text-blue-400 animate-bounce" />,
          color: 'blue-400',
          position: 'bottom-8 left-2 sm:bottom-8 sm:left-2',
          animationDelay: '4s'
        },
        {
          id: 'completed',
          text: 'Call Done',
          icon: <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>,
          color: 'green-400',
          position: 'bottom-8 right-2 sm:bottom-8 sm:right-2',
          animationDelay: '5s'
        }
      ]
    },
    {
      id: 3,
      name: 'WhatsApp Agent',
      description: 'Sends WhatsApp Messages',
      monthlyOutput: '60,000 messages / month',
      targetNumber: 60000,
      unit: 'messages',
      image: '/whatsappagent.png',
      icon: MessageSquare,
      processSteps: [
        {
          id: 'drafting',
          text: 'Drafting',
          icon: <svg className="w-3 h-3 text-brand-blue animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>,
          color: 'brand-blue',
          position: 'top-2 left-2 sm:top-2 sm:left-2',
          animationDelay: '0s'
        },
        {
          id: 'sending',
          text: 'Sending',
          icon: <svg className="w-3 h-3 text-green-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>,
          color: 'green-400',
          position: 'top-2 right-2 sm:top-2 sm:right-2',
          animationDelay: '1s'
        },
        {
          id: 'delivered',
          text: 'Delivered',
          icon: <svg className="w-3 h-3 text-yellow-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>,
          color: 'yellow-400',
          position: 'top-14 left-2 sm:top-16 sm:left-2',
          animationDelay: '2s'
        },
        {
          id: 'read',
          text: 'Read',
          icon: <svg className="w-3 h-3 text-blue-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>,
          color: 'blue-400',
          position: 'top-14 right-2 sm:top-16 sm:right-2',
          animationDelay: '3s'
        },
        {
          id: 'replying',
          text: 'Reply Received',
          icon: <MessageCircle className="w-3 h-3 text-purple-400 animate-pulse" />,
          color: 'purple-400',
          position: 'bottom-8 left-2 sm:bottom-8 sm:left-2',
          animationDelay: '4s'
        },
        {
          id: 'engaging',
          text: 'Engaging',
          icon: <svg className="w-3 h-3 text-brand-green-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z" />
          </svg>,
          color: 'brand-green-teal',
          position: 'bottom-8 right-2 sm:bottom-8 sm:right-2',
          animationDelay: '5s'
        }
      ]
    },
    {
      id: 4,
      name: 'Email Agent',
      description: 'Sends Email Campaigns',
      monthlyOutput: '100,000 emails / month',
      targetNumber: 100000,
      unit: 'emails',
      image: '/emailagent.png',
      icon: Mail,
      processSteps: [
        {
          id: 'composing',
          text: 'Composing',
          icon: <svg className="w-3 h-3 text-brand-blue animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>,
          color: 'brand-blue',
          position: 'top-2 left-2 sm:top-2 sm:left-2',
          animationDelay: '0s'
        },
        {
          id: 'personalizing',
          text: 'Personalizing',
          icon: <svg className="w-3 h-3 text-purple-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>,
          color: 'purple-400',
          position: 'top-2 right-2 sm:top-2 sm:right-2',
          animationDelay: '1s'
        },
        {
          id: 'sending-email',
          text: 'Sending',
          icon: <Mail className="w-3 h-3 text-green-400 animate-bounce" />,
          color: 'green-400',
          position: 'top-14 left-2 sm:top-16 sm:left-2',
          animationDelay: '2s'
        },
        {
          id: 'tracking',
          text: 'Tracking',
          icon: <svg className="w-3 h-3 text-yellow-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>,
          color: 'yellow-400',
          position: 'top-14 right-2 sm:top-16 sm:right-2',
          animationDelay: '3s'
        },
        {
          id: 'opened',
          text: 'Email Opened',
          icon: <svg className="w-3 h-3 text-blue-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>,
          color: 'blue-400',
          position: 'bottom-8 left-2 sm:bottom-8 sm:left-2',
          animationDelay: '4s'
        },
        {
          id: 'clicked',
          text: 'Link Clicked',
          icon: <svg className="w-3 h-3 text-brand-green-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>,
          color: 'brand-green-teal',
          position: 'bottom-8 right-2 sm:bottom-8 sm:right-2',
          animationDelay: '5s'
        }
      ]
    },
    {
      id: 5,
      name: 'LinkedIn Agent',
      description: 'LinkedIn Outreach & Networking',
      monthlyOutput: '10,000 connections / month',
      targetNumber: 10000,
      unit: 'connections',
      image: '/linkedinagent.png',
      icon: Linkedin,
      processSteps: [
        {
          id: 'viewing',
          text: 'Profile View',
          icon: <svg className="w-3 h-3 text-brand-blue animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>,
          color: 'brand-blue',
          position: 'top-2 left-2 sm:top-2 sm:left-2',
          animationDelay: '0s'
        },
        {
          id: 'connecting',
          text: 'Connect Request',
          icon: <svg className="w-3 h-3 text-green-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>,
          color: 'green-400',
          position: 'top-2 right-2 sm:top-2 sm:right-2',
          animationDelay: '1s'
        },
        {
          id: 'accepted',
          text: 'Accepted',
          icon: <svg className="w-3 h-3 text-yellow-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>,
          color: 'yellow-400',
          position: 'top-14 left-2 sm:top-16 sm:left-2',
          animationDelay: '2s'
        },
        {
          id: 'messaging',
          text: 'Messaging',
          icon: <MessageCircle className="w-3 h-3 text-purple-400 animate-bounce" />,
          color: 'purple-400',
          position: 'top-14 right-2 sm:top-16 sm:right-2',
          animationDelay: '3s'
        },
        {
          id: 'engaging-linkedin',
          text: 'Engaging',
          icon: <svg className="w-3 h-3 text-blue-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>,
          color: 'blue-400',
          position: 'bottom-8 left-2 sm:bottom-8 sm:left-2',
          animationDelay: '4s'
        },
        {
          id: 'qualified-linkedin',
          text: 'Qualified',
          icon: <svg className="w-3 h-3 text-brand-green-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>,
          color: 'brand-green-teal',
          position: 'bottom-8 right-2 sm:bottom-8 sm:right-2',
          animationDelay: '5s'
        }
      ]
    },
    {
      id: 6,
      name: 'Follow Up Agent',
      description: 'Nurtures & Re-engages Prospects',
      monthlyOutput: '15,000 follow-ups / month',
      targetNumber: 15000,
      unit: 'follow-ups',
      image: '/followupagent.png',
      icon: RotateCcw,
      processSteps: [
        {
          id: 'identifying',
          text: 'Identifying',
          icon: <Search className="w-3 h-3 text-brand-blue animate-pulse" />,
          color: 'brand-blue',
          position: 'top-2 left-2 sm:top-2 sm:left-2',
          animationDelay: '0s'
        },
        {
          id: 'sequencing',
          text: 'Sequencing',
          icon: <svg className="w-3 h-3 text-purple-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>,
          color: 'purple-400',
          position: 'top-2 right-2 sm:top-2 sm:right-2',
          animationDelay: '1s'
        },
        {
          id: 'nurturing',
          text: 'Nurturing',
          icon: <svg className="w-3 h-3 text-green-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>,
          color: 'green-400',
          position: 'top-14 left-2 sm:top-16 sm:left-2',
          animationDelay: '2s'
        },
        {
          id: 'timing',
          text: 'Perfect Timing',
          icon: <svg className="w-3 h-3 text-yellow-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>,
          color: 'yellow-400',
          position: 'top-14 right-2 sm:top-16 sm:right-2',
          animationDelay: '3s'
        },
        {
          id: 'reengaging',
          text: 'Re-engaging',
          icon: <RotateCcw className="w-3 h-3 text-blue-400 animate-bounce" />,
          color: 'blue-400',
          position: 'bottom-8 left-2 sm:bottom-8 sm:left-2',
          animationDelay: '4s'
        },
        {
          id: 'handoff',
          text: 'Hand-off',
          icon: <svg className="w-3 h-3 text-brand-green-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>,
          color: 'brand-green-teal',
          position: 'bottom-8 right-2 sm:bottom-8 sm:right-2',
          animationDelay: '5s'
        }
      ]
    }
  ];

  // Initialize counters for all agents
  const agent1Count = useCounter(agents[0].targetNumber, 4000, 1);
  const agent2Count = useCounter(agents[1].targetNumber, 3500, 2);
  const agent3Count = useCounter(agents[2].targetNumber, 3200, 3);
  const agent4Count = useCounter(agents[3].targetNumber, 2800, 4);
  const agent5Count = useCounter(agents[4].targetNumber, 2500, 5);
  const agent6Count = useCounter(agents[5].targetNumber, 4200, 6);

  const getCurrentCount = (agentId: number) => {
    switch(agentId) {
      case 1: return agent1Count;
      case 2: return agent2Count;
      case 3: return agent3Count;
      case 4: return agent4Count;
      case 5: return agent5Count;
      case 6: return agent6Count;
      default: return 0;
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <section className="relative py-8 sm:py-12 lg:py-16 px-4 sm:px-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Headline */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-text-primary font-black text-2xl sm:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4 tracking-tight">
            Meet Your AI Sales Team
          </h2>
          <div className="w-16 sm:w-20 lg:w-24 h-1 bg-gradient-brand mx-auto"></div>
        </div>

        {/* Agent Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-6xl mx-auto">
          {agents.map((agent, index) => (
            <div
              key={agent.id}
              className="relative bg-bg-surface border border-card-border rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 shadow-2xl overflow-hidden group hover:scale-105 transition-all duration-300 hover:shadow-glow-combined"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Agent Process Visualization */}
              <AgentProcessVisualizer 
                agentType={agent.name.toLowerCase().replace(' ', '-')}
                steps={agent.processSteps}
              />

              {/* Agent Photo */}
              <div className="mb-4 sm:mb-5 lg:mb-6">
                <div className="w-full h-44 sm:h-40 lg:h-48 rounded-lg sm:rounded-xl overflow-hidden bg-chip-bg border border-chip-border">
                  <img
                    src={`/landing-page${agent.image}`}
                    alt={agent.name}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              {/* Agent Name */}
              <div className="mb-3 sm:mb-4">
                <h3 className="text-text-primary font-bold text-lg sm:text-xl text-center">
                  {agent.name}
                </h3>
              </div>

              {/* What Agent Does */}
              <div className="mb-4 sm:mb-5 lg:mb-6">
                <p className="text-text-secondary text-sm sm:text-base text-center leading-relaxed">
                  {agent.description}
                </p>
              </div>

              {/* Monthly Output */}
              <div className="bg-chip-bg border border-chip-border rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <agent.icon className="w-4 h-4 sm:w-5 sm:h-5 text-brand-blue flex-shrink-0" />
                  <div className="text-white font-bold text-sm sm:text-base lg:text-lg">
                    <span className="tabular-nums">
                      {formatNumber(getCurrentCount(agent.id))} {agent.unit} / month
                    </span>
                  </div>
                </div>
              </div>

              {/* Subtle background gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/8 to-brand-green-teal/8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-xl sm:rounded-2xl" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AISalesTeamSection;